import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Modal } from 'semantic-ui-react';

import selectors from '../../selectors';

import styles from './PointSystemTransferModal.module.scss';

const PointSystemTransferModal = React.memo(
  ({ open, mode, templateName, onConfirm, onRefuse, onClose }) => {
    const [t] = useTranslation();
    const name = templateName || '';
    const transferStatus = useSelector(selectors.selectCardTransferStatus);
    const isSubmitting = transferStatus?.status === 'pending';
    const errorMessage =
      transferStatus?.status === 'failure' ? transferStatus.message : null;

    const handleConfirm = useCallback(
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isSubmitting) {
          return;
        }
        onConfirm();
      },
      [onConfirm, isSubmitting],
    );

    const handleRefuse = useCallback(
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isSubmitting) {
          return;
        }
        if (onRefuse) {
          onRefuse();
        } else {
          onClose();
        }
      },
      [onRefuse, onClose, isSubmitting],
    );

    const handleClose = useCallback(
      (event) => {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        if (isSubmitting) {
          return;
        }
        onClose();
      },
      [onClose, isSubmitting],
    );

    const isEnablePrompt = mode === 'enable_and_apply';
    const isHiddenNotice = mode === 'hidden_notice';

    return (
      <Modal
        open={open}
        size="small"
        centered
        closeOnDimmerClick={false}
        className={styles.modal}
        onClose={handleClose}
      >
        <Modal.Header>
          {isHiddenNotice
            ? t('common.pointSystemTransferHiddenTitle', {
                defaultValue: 'Начисления сохранены',
              })
            : t('common.pointSystemTransferEnableTitle', {
                defaultValue: 'Балльная система выключена',
              })}
        </Modal.Header>
        <Modal.Content>
          <p className={styles.text}>
            {isHiddenNotice
              ? t('common.pointSystemTransferHiddenDescription', {
                  templateName: name,
                  defaultValue:
                    'Начисления сохранены в задаче, но сейчас не отображаются. Чтобы увидеть их, включите на этой доске балльную систему с шаблоном «{{templateName}}» или верните задачу на доску с этим шаблоном',
                })
              : t('common.pointSystemTransferEnableDescription', {
                  templateName: name,
                  defaultValue:
                    'На этой доске балльная система выключена. Включить её и применить шаблон «{{templateName}}»?',
                })}
          </p>
          {errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
        </Modal.Content>
        <Modal.Actions className={styles.actions}>
          {isEnablePrompt && (
            <>
              <Button type="button" onClick={handleRefuse} disabled={isSubmitting}>
                {t('action.no', { defaultValue: 'Нет' })}
              </Button>
              <Button
                type="button"
                positive
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={handleConfirm}
              >
                {t('action.yes', { defaultValue: 'Да' })}
              </Button>
            </>
          )}
          {isHiddenNotice && (
            <Button type="button" positive onClick={handleClose}>
              {t('action.ok', { defaultValue: 'OK' })}
            </Button>
          )}
        </Modal.Actions>
      </Modal>
    );
  },
);

PointSystemTransferModal.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['enable_and_apply', 'hidden_notice']).isRequired,
  templateName: PropTypes.string,
  onConfirm: PropTypes.func,
  onRefuse: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};

PointSystemTransferModal.defaultProps = {
  templateName: '',
  onConfirm: undefined,
  onRefuse: undefined,
};

export default PointSystemTransferModal;
